// CHANGE IT WHERE IT IS SHOWN (v4.19.4, Karan 28/10/2026 on 8451: "need to
// make it easier to change leads etc on projects too").
//
// The everyday fields on the project record — lead, checker, site address and
// the deadline — open a small popover right where the value is shown, instead
// of a trip through ⋯ › Edit details. This file is the popovers (the pure
// rules are inline-edit-rules.js); the WRITES stay the project page's own (the same
// updateProjectFields / task / change-list calls it already makes), so nothing
// here invents a data path.
//
// One open at a time. Enter saves (or picks the highlighted person), Esc
// cancels, and focus goes back to the value that opened it. Every value is
// rendered through escapeHtml (house rule 0).
import { escapeHtml } from '../ui.js';
import { attachPlacesAutocomplete } from '../places-ui.js';
import { filterPeople, dayValue } from '../inline-edit-rules.js';

export { pickablePeople, filterPeople, personName, personChange, postcodeIn, siteFields, dayValue } from '../inline-edit-rules.js';

// ── The popover ─────────────────────────────────────────────────────────────

let current = null;   // { el, close }

export function closeInlineEdit() { if (current) current.close(false); }

function mountPopover(anchor, html, { label = '', width = 280, form = false } = {}) {
  closeInlineEdit();
  const el = document.createElement('div');
  el.className = form ? 'pinl pinl-form' : 'menu-v4 pmenu pinl';
  el.setAttribute('role', 'dialog');
  if (label) el.setAttribute('aria-label', label);
  el.style.width = `${width}px`;
  el.innerHTML = html;
  document.body.appendChild(el);

  const place = () => {
    const r = anchor.getBoundingClientRect();
    const mw = el.offsetWidth, mh = el.offsetHeight;
    let top = r.bottom + 6;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 6);
    el.style.left = `${Math.round(Math.max(8, Math.min(r.left, window.innerWidth - mw - 8)))}px`;
    el.style.top = `${Math.round(top)}px`;
  };
  place();

  let closed = false;
  const onDoc = (ev) => { if (!el.contains(ev.target) && !anchor.contains(ev.target)) close(false); };
  const onResize = () => close(false);
  const t = setTimeout(() => document.addEventListener('pointerdown', onDoc, true), 0);
  window.addEventListener('resize', onResize);
  function close(refocus = true) {
    if (closed) return;
    closed = true;
    clearTimeout(t);
    document.removeEventListener('pointerdown', onDoc, true);
    window.removeEventListener('resize', onResize);
    el.remove();
    if (current && current.el === el) current = null;
    if (refocus && anchor.isConnected && anchor.focus) anchor.focus();
  }
  // Esc anywhere inside cancels and hands focus back.
  el.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(true); }
  });
  current = { el, close };
  return { el, close, place };
}

// A save in flight: the popover says so, and stays open with the reason if it fails.
async function running(el, fn, close) {
  const err = el.querySelector('.pinl-err');
  el.classList.add('is-busy');
  el.querySelectorAll('button, input').forEach((b) => { b.disabled = true; });
  if (err) { err.hidden = false; err.classList.remove('bad'); err.textContent = 'Saving…'; }
  try {
    await fn();
    close(false);
    return true;
  } catch (e) {
    el.classList.remove('is-busy');
    el.querySelectorAll('button, input').forEach((b) => { b.disabled = false; });
    if (err) { err.hidden = false; err.classList.add('bad'); err.textContent = `Couldn’t save — ${(e && e.message) || e}`; }
    return false;
  }
}

/**
 * The people picker: a search box over active staff, the current one marked.
 *   people     pickablePeople(users)
 *   currentId  who holds it now ('' for nobody)
 *   heading    'Lead' | 'Checker'
 *   removeLabel 'Remove lead' — offered only while someone holds it
 *   extra      [{ label, onClick }] — e.g. "See Rajan's projects"
 *   onPick(id) → Promise; id '' means remove. Resolves → the popover closes.
 */
export function openPersonPicker(anchor, { people = [], currentId = '', heading = 'Person', removeLabel = '', extra = [], onPick } = {}) {
  const cur = String(currentId || '').toLowerCase();
  const { el, close } = mountPopover(anchor, `
    <div class="pmenu-h">${escapeHtml(heading)}</div>
    <input type="search" class="f-input pinl-q" placeholder="Search people" aria-label="Search people" autocomplete="off" spellcheck="false">
    <div class="pinl-list" role="listbox" aria-label="${escapeHtml(heading)}"></div>
    ${cur && removeLabel ? `<hr><button type="button" data-remove><span class="pmenu-l">${escapeHtml(removeLabel)}</span></button>` : ''}
    ${extra.length ? `<hr>${extra.map((x, i) => `<button type="button" data-x="${i}"><span class="pmenu-l">${escapeHtml(x.label)}</span></button>`).join('')}` : ''}
    <div class="pinl-err" role="status" hidden></div>`, { label: `Change ${heading.toLowerCase()}`, width: 280 });
  const q = el.querySelector('.pinl-q');
  const list = el.querySelector('.pinl-list');
  let shown = [], act = 0;
  const paint = () => {
    shown = filterPeople(people, q.value);
    if (act >= shown.length) act = Math.max(0, shown.length - 1);
    list.innerHTML = shown.length
      ? shown.map((p, i) => `<button type="button" role="option" data-k="${escapeHtml(p.id)}" aria-selected="${String(p.id).toLowerCase() === cur}"
          class="${[String(p.id).toLowerCase() === cur ? 'is-now' : '', i === act ? 'is-act' : ''].filter(Boolean).join(' ')}"><span class="pmenu-l">${escapeHtml(p.name)}</span>${String(p.id).toLowerCase() === cur ? '<span class="pmenu-hint">now</span>' : ''}</button>`).join('')
      : '<p class="pinl-none">Nobody matches.</p>';
    const a = list.querySelector('.is-act');
    if (a && a.scrollIntoView) a.scrollIntoView({ block: 'nearest' });
  };
  // Start on the current holder, so Enter on an untouched picker changes nothing.
  act = Math.max(0, people.findIndex((p) => String(p.id).toLowerCase() === cur));
  paint();
  const pick = (id) => {
    if (String(id || '').toLowerCase() === cur) { close(true); return; }
    running(el, () => onPick(id), close);
  };
  q.addEventListener('input', () => { act = 0; paint(); });
  q.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      if (!shown.length) return;
      act = ev.key === 'ArrowDown' ? (act + 1) % shown.length : (act <= 0 ? shown.length - 1 : act - 1);
      paint();
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (shown[act]) pick(shown[act].id);
    }
  });
  list.addEventListener('click', (ev) => { const b = ev.target.closest('button[data-k]'); if (b) pick(b.dataset.k); });
  el.querySelector('[data-remove]')?.addEventListener('click', () => pick(''));
  el.querySelectorAll('[data-x]').forEach((b) => b.addEventListener('click', () => {
    const x = extra[+b.dataset.x]; close(false); if (x && x.onClick) x.onClick();
  }));
  q.focus();
  return el;
}

/**
 * The site address: a Google Places search (places-ui.js, the same server-side
 * search as Bring in a project). Picking Google's match places the site; Enter
 * saves (with suggestions showing, Enter takes the first one).
 *   onSave(text, meta) → Promise   meta: { lat, lng, placeId, postcode }
 */
export function openAddressEditor(anchor, { value = '', meta = null, onSave } = {}) {
  const { el, close } = mountPopover(anchor, `
    <div class="pmenu-h">Site address</div>
    <div class="fc-picker pinl-pick">
      <input type="text" class="f-input pinl-in" value="${escapeHtml(value)}" placeholder="Start typing — Google suggests the address" aria-label="Site address" autocomplete="off" spellcheck="false">
      <div class="fc-dropdown" hidden></div>
    </div>
    <p class="pinl-note">Pick Google’s match to place the site. The site checks run again by themselves.</p>
    <div class="pinl-acts">
      <button type="button" class="btn sm primary" data-save>Save</button>
      <button type="button" class="btn sm" data-cancel>Cancel</button>
      ${value ? '<span class="pinl-fill"></span><button type="button" class="pd4-link pinl-rm" data-clear>Remove</button>' : ''}
    </div>
    <div class="pinl-err" role="status" hidden></div>`, { label: 'Change the site address', width: 360, form: true });
  const input = el.querySelector('.pinl-in');
  const drop = el.querySelector('.fc-dropdown');
  const ac = attachPlacesAutocomplete(input, drop, { initial: meta || {} });
  const orig = String(value || '').trim();
  const save = (text) => {
    const t = String(text == null ? input.value : text).trim();
    // Unchanged text and no new Google match → nothing to write.
    const picked = !!(ac.meta && ac.meta.placeId && ac.meta.placeId !== (meta && meta.placeId));
    if (text == null && t === orig && !picked) { close(true); return; }
    running(el, () => onSave(t, t ? { ...ac.meta } : {}), close);
  };
  input.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    const first = !drop.hidden && drop.querySelector('.fc-opt');
    if (first) { first.click(); return; }
    save();
  });
  el.querySelector('[data-save]').addEventListener('click', () => save());
  el.querySelector('[data-cancel]').addEventListener('click', () => close(true));
  el.querySelector('[data-clear]')?.addEventListener('click', () => save(''));
  input.focus();
  input.setSelectionRange(input.value.length, input.value.length);
  return el;
}

/**
 * A date (the deadline). Enter saves; "No deadline" clears it.
 *   onSave('YYYY-MM-DD' | '') → Promise
 */
export function openDateEditor(anchor, { value = '', heading = 'Deadline', clearLabel = 'No deadline', onSave } = {}) {
  const v0 = dayValue(value);
  const { el, close } = mountPopover(anchor, `
    <div class="pmenu-h">${escapeHtml(heading)}</div>
    <input type="date" class="f-input pinl-in" value="${escapeHtml(v0)}" aria-label="${escapeHtml(heading)}">
    <div class="pinl-acts">
      <button type="button" class="btn sm primary" data-save>Save</button>
      <button type="button" class="btn sm" data-cancel>Cancel</button>
      ${v0 ? `<span class="pinl-fill"></span><button type="button" class="pd4-link pinl-rm" data-clear>${escapeHtml(clearLabel)}</button>` : ''}
    </div>
    <div class="pinl-err" role="status" hidden></div>`, { label: `Change the ${heading.toLowerCase()}`, width: 280, form: true });
  const input = el.querySelector('.pinl-in');
  const save = (v) => {
    const next = v == null ? String(input.value || '') : v;
    if (next === v0) { close(true); return; }
    running(el, () => onSave(next), close);
  };
  input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } });
  el.querySelector('[data-save]').addEventListener('click', () => save());
  el.querySelector('[data-cancel]').addEventListener('click', () => close(true));
  el.querySelector('[data-clear]')?.addEventListener('click', () => save(''));
  input.focus();
  return el;
}

/**
 * One line of text (Assets' record values, docs/design_handoff_assets): the
 * value, Save and Cancel; Enter saves, Esc cancels. onSave(text) → Promise —
 * a rejection stays open with its reason.
 */
export function openTextEditor(anchor, { value = '', heading = 'Value', maxLength = 120, onSave } = {}) {
  const v0 = String(value || '');
  const { el, close } = mountPopover(anchor, `
    <div class="pmenu-h">${escapeHtml(heading)}</div>
    <input type="text" class="f-input pinl-in" value="${escapeHtml(v0)}" maxlength="${Number(maxLength) || 120}" aria-label="${escapeHtml(heading)}" autocomplete="off" spellcheck="false">
    <div class="pinl-acts">
      <button type="button" class="btn sm primary" data-save>Save</button>
      <button type="button" class="btn sm" data-cancel>Cancel</button>
    </div>
    <div class="pinl-err" role="status" hidden></div>`, { label: `Change ${heading.toLowerCase()}`, width: 320, form: true });
  const input = el.querySelector('.pinl-in');
  const save = () => {
    const next = String(input.value || '').trim();
    if (next === v0.trim()) { close(true); return; }
    running(el, () => onSave(next), close);
  };
  input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } });
  el.querySelector('[data-save]').addEventListener('click', save);
  el.querySelector('[data-cancel]').addEventListener('click', () => close(true));
  input.focus();
  input.select();
  return el;
}

/**
 * A list of references (project-refs.js: planning, building control, client
 * POs). The ones on file, each with Remove; a box to add another (with an
 * optional note and, for POs, the instruction it is for). Enter in the box adds
 * it; Save writes the list; Esc cancels and nothing changes.
 *   items        the current list [{ ref, note?, instructionSeq? }]
 *   instructions [{ seq, label }] — offered for POs only (withInstruction)
 *   addRef(list, entry) → list    (project-refs.js addRef, bound by the caller)
 *   onSave(list) → Promise
 */
export function openRefsEditor(anchor, { heading = 'References', items = [], placeholder = '', withInstruction = false, instructions = [], addRef, onSave } = {}) {
  let list = (items || []).slice();
  const v0 = JSON.stringify(list);
  const instrOpts = withInstruction && instructions.length
    ? `<select class="f-input pinl-in pinl-seq" aria-label="Which instruction it is for"><option value="">Whole project</option>${instructions.map((i) => `<option value="${escapeHtml(String(i.seq))}">${escapeHtml(i.label)}</option>`).join('')}</select>` : '';
  const { el, close, place } = mountPopover(anchor, `
    <div class="pmenu-h">${escapeHtml(heading)}</div>
    <div class="pinl-refs"></div>
    <input type="text" class="f-input pinl-in pinl-ref" maxlength="60" placeholder="${escapeHtml(placeholder || 'Reference')}" aria-label="Add a reference" autocomplete="off" spellcheck="false">
    <input type="text" class="f-input pinl-in pinl-rnote" maxlength="120" placeholder="Note (optional)" aria-label="Note" autocomplete="off">
    ${instrOpts}
    <div class="pinl-acts">
      <button type="button" class="btn sm primary" data-save>Save</button>
      <button type="button" class="btn sm" data-cancel>Cancel</button>
      <span class="pinl-fill"></span><button type="button" class="pd4-link" data-add>Add another</button>
    </div>
    <div class="pinl-err" role="status" hidden></div>`, { label: `Change ${heading.toLowerCase()}`, width: 340, form: true });
  const host = el.querySelector('.pinl-refs');
  const refIn = el.querySelector('.pinl-ref');
  const noteIn = el.querySelector('.pinl-rnote');
  const seqIn = el.querySelector('.pinl-seq');
  const seqLabel = (s) => { const i = instructions.find((x) => Number(x.seq) === Number(s)); return i ? i.label : `Instruction ${s}`; };
  const paint = () => {
    host.innerHTML = list.length
      ? list.map((x, i) => `<div class="pinl-refrow"><span class="pinl-refmain"><b>${escapeHtml(x.ref)}</b>${x.note || x.instructionSeq ? `<span class="pinl-refsub">${escapeHtml([x.note || '', x.instructionSeq ? seqLabel(x.instructionSeq) : ''].filter(Boolean).join(' · '))}</span>` : ''}</span><button type="button" class="pd4-link pinl-rm" data-rm="${i}" aria-label="Remove ${escapeHtml(x.ref)}">Remove</button></div>`).join('')
      : '<p class="pinl-none">None yet.</p>';
    place();
  };
  // Whatever is typed in the box is added (so Save with a typed reference keeps it).
  const take = () => {
    const ref = String(refIn.value || '').trim();
    if (!ref) return false;
    list = addRef(list, { ref, note: noteIn.value, instructionSeq: seqIn ? seqIn.value : null });
    refIn.value = ''; noteIn.value = ''; if (seqIn) seqIn.value = '';
    paint();
    return true;
  };
  const save = () => {
    take();
    if (JSON.stringify(list) === v0) { close(true); return; }
    running(el, () => onSave(list), close);
  };
  host.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-rm]');
    if (!b) return;
    list = list.filter((_, i) => i !== Number(b.dataset.rm));
    paint();
    refIn.focus();
  });
  [refIn, noteIn].forEach((inp) => inp.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    if (!take()) save(); else refIn.focus();
  }));
  el.querySelector('[data-add]').addEventListener('click', () => { take(); refIn.focus(); });
  el.querySelector('[data-save]').addEventListener('click', save);
  el.querySelector('[data-cancel]').addEventListener('click', () => close(true));
  paint();
  refIn.focus();
  return el;
}
